// src/pages/About/About.jsx (path example)
import React, { useEffect, useState } from "react";
import { getStoredData } from "../../api/fetchData"; // ⬅️ adjust path if needed

function Pill({ children }) {
  return (
    <span className="inline-flex items-center rounded-full bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-700 ring-1 ring-inset ring-indigo-200">
      {children}
    </span>
  );
}

function Stat({ value, label, sub }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="text-3xl font-bold tabular-nums">{value}</div>
      <div className="mt-1 text-sm text-gray-600">{label}</div>
      {sub ? <div className="mt-1 text-xs text-gray-400">{sub}</div> : null}
    </div>
  );
}

function Feature({ title, children }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <div className="text-sm font-semibold text-gray-900">{title}</div>
      <div className="mt-2 text-sm leading-relaxed text-gray-600">
        {children}
      </div>
    </div>
  );
}

const About = () => {
  const contactEmail =
    process.env.REACT_APP_CONTACT_EMAIL || "axiomos2024@gmail.com";

  const [aboutConfig, setAboutConfig] = useState(null);

  useEffect(() => {
    // Read from localStorage via helper
    const stored = getStoredData();
    let pageConfig = null;
    pageConfig = stored[2].AboutPage;

    setAboutConfig(pageConfig);
  }, []);

  // Simple loading / fallback
  if (!aboutConfig) {
    return (
      <main className="mx-auto max-w-7xl px-4 py-10">
        <p className="text-sm text-gray-500">Loading About page…</p>
      </main>
    );
  }

  const {
    hero,
    stats,
    primaryFeatures,
    whyFeatures,
    howItWorks,
    security,
    roadmap,
    cta,
  } = aboutConfig;

  return (
    <main className="mx-auto max-w-7xl px-4 py-10">
      {/* Hero */}
      <section
        className="rounded-3xl border border-gray-200 bg-white p-8 shadow-sm"
        aria-labelledby="about-hero-title"
      >
        <div className="flex flex-wrap items-center justify-between gap-4">
          <Pill>{hero.badge}</Pill>
          <p className="text-xs text-gray-500">{hero.tagline}</p>
        </div>

        <h1
          id="about-hero-title"
          className="mt-4 text-3xl font-bold leading-tight text-gray-900 md:text-4xl"
        >
          {hero.title}
        </h1>
        <p className="mt-3 max-w-3xl text-sm text-gray-600 md:text-base">
          {hero.description}
        </p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {hero.pills?.map((pill) => (
            <Pill key={pill}>{pill}</Pill>
          ))}
        </div>
      </section>

      {/* Stats */}
      <section
        className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
        aria-label="Key product statistics"
      >
        {stats?.map((s) => (
          <Stat key={s.label} value={s.value} label={s.label} sub={s.sub} />
        ))}
      </section>

      {/* What we do */}
      <section
        className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-3"
        aria-label="Core capabilities"
      >
        {primaryFeatures?.map((f) => (
          <Feature key={f.title} title={f.title}>
            {f.body}
          </Feature>
        ))}
      </section>

      {/* How it works */}
      <section className="mt-10 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">
          {howItWorks.title}
        </h2>
        <ol
          className="mt-3 list-decimal space-y-2 pl-5 text-sm text-gray-700"
          aria-label="How the platform works"
        >
          {howItWorks.steps?.map((step) => (
            <li key={step.label}>
              <span className="font-medium text-gray-900">{step.label}:</span>{" "}
              {step.description}
            </li>
          ))}
        </ol>
      </section>

      {/* Why teams choose us */}
      <section
        className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2"
        aria-label="Why teams choose this platform"
      >
        {whyFeatures?.map((f) => (
          <Feature key={f.title} title={f.title}>
            {f.body}
          </Feature>
        ))}
      </section>

      {/* Security & Compliance */}
      <section className="mt-10 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">
          {security.title}
        </h2>
        <ul className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-700 sm:grid-cols-2">
          {security.items?.map((item) => (
            <li key={item}>• {item}</li>
          ))}
        </ul>
      </section>

      {/* Roadmap */}
      <section className="mt-10 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">{roadmap.title}</h2>
        <div className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
          {roadmap.items?.map((item) => (
            <div
              key={item.label}
              className="rounded-xl bg-gray-50 p-4 transition hover:-translate-y-0.5 hover:shadow-sm"
            >
              <div className="font-medium text-gray-900">{item.label}</div>
              <div className="mt-1 text-gray-600">{item.text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-10 rounded-3xl border border-gray-200 bg-gradient-to-br from-indigo-50 to-white p-6 shadow-sm">
        <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{cta.title}</h3>
            <p className="mt-1 text-xs text-gray-600">{cta.description}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a
              href={`mailto:${contactEmail}?subject=${encodeURIComponent(
                cta.primaryButtonSubject
              )}`}
              className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {cta.primaryButtonLabel}
            </a>
            <a
              href={cta.secondaryButtonHref}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              {cta.secondaryButtonLabel}
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
